"use client";

import Loader from '@/components/common/Loader';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface QuoteItem {
  id: string | undefined;
  name: string | undefined;
  slug?: string;
  image?: string;
  images?: string;
  quantity: number;
  addedAt: Date;
}

interface QuoteContextType {
  quoteItems: QuoteItem[];
  addToQuote: (item: QuoteItem) => void;
  removeFromQuote: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearQuote: () => void;
  quoteCount: number;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

export const QuoteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load quote items from localStorage on component mount
  useEffect(() => {
    try {
      const savedQuoteItems = localStorage.getItem('quoteItems');
      if (savedQuoteItems) {
        const parsedItems = JSON.parse(savedQuoteItems);
        setQuoteItems(parsedItems.map((item: QuoteItem & { addedAt: string }) => ({
          ...item,
          quantity: item.quantity || 1, // Ensure quantity exists for older items
          addedAt: new Date(item.addedAt)
        })));
      }
    } catch (error) {
      console.error('Error loading quote items from localStorage:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save quote items to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('quoteItems', JSON.stringify(quoteItems));
      } catch (error) {
        console.error('Error saving quote items to localStorage:', error);
      }
    }
  }, [quoteItems, isLoaded]);

  const addToQuote = (item: QuoteItem) => {
    setQuoteItems(prev => {
      // Check if item already exists
      const existingIndex = prev.findIndex(quoteItem => quoteItem.id === item.id);
      if (existingIndex !== -1) {
        // If exists, increment quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1
        };
        return updated;
      }
      // If doesn't exist, add with quantity 1
      return [...prev, { ...item, quantity: item.quantity || 1, addedAt: new Date() }];
    });
  };

  const removeFromQuote = (id: string) => {
    setQuoteItems(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromQuote(id);
      return;
    }
    
    setQuoteItems(prev => 
      prev.map(item => 
        item.id === id ? { ...item, quantity } : item
      )
    );
  };

  const clearQuote = () => {
    setQuoteItems([]);
    try {
      localStorage.removeItem('quoteItems');
    } catch (error) {
      console.error('Error clearing quote items from localStorage:', error);
    }
  };

  const value: QuoteContextType = {
    quoteItems,
    addToQuote,
    removeFromQuote,
    updateQuantity,
    clearQuote,
    quoteCount: quoteItems.length,
  };

  // Don't render until we've loaded from localStorage
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          {/* <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-PrimaryLightBlue mx-auto"></div> */}
          {/* <p className="mt-2 text-gray-600">Loading...</p> */}
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <QuoteContext.Provider value={value}>
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (context === undefined) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
}; 